/**
 * 전장의 낱소리를 묶는다. 판은 틱마다 "칼이 몇 번, 창이 몇 번, 화살이 몇 발, 몇이 쓰러졌다"와 그 자리를 내놓고
 * (`World.fx`), 여기서 그것을 **알갱이 몇 개**와 **깔개의 크기**로 바꾼다. Web Audio를 모르는 순수한 셈이라 시험에서는
 * 가짜 난수로 돌린다(`tests/audio.test.ts`).
 *
 * 큰 판은 1초에 타격이 90번쯤 난다(깊이 20, 아군 50·적 110 — 틱에 많게는 열 번). 하나씩 내면 소리가 뭉개지고 겹쳐
 * 커지기만 한다. 그래서:
 * - 종류마다 한 틱에 `cap`개까지, `group`번을 한 알갱이로 묶는다. 많이 부딪칠수록 그 알갱이가 조금 커진다.
 * - 최근 `WINDOW_MS` 안에 낸 알갱이가 `win`개면 더 내지 않는다(전체 상한). 프레임이 아니라 시간으로 센다.
 * - 못 낸 것을 줄 세워 뒤늦게 내지 않는다 — 버린다. 깔개가 그 몫을 메운다: 최근 타격의 잦기를 따라 커진다.
 *   (싸우는 **인원**이 아니라 **타격**을 따른다. 인원을 따르면 행군 중에도 난전 소리가 난다 — 계획 리뷰.)
 * - 자리: 그 틱에 그 종류가 난 한가운데(`fx`의 합 ÷ 수)를 화면의 좌우와 거리로 바꾼다. 멀수록 작다.
 *   밤에는 자리를 싣지 않는다(`flat`) — 좌우도 거리도. 보이지 않는 적 궁수의 자리가 소리로 드러나면 안 된다.
 *   처음에는 좌우만 뺐는데, 거리에 따른 크기가 남아 화면을 옮겨 가며 들으면 가까워지는 쪽을 좁힐 수 있었다
 *   (같은 발사가 0px에서 0.68, 900px에서 0.17 — 리뷰·QA). 종류별로 양편을 합친 셈이라 숨은 쪽의 몫만 가려낼 수도 없다.
 */
import { FX, FX_KINDS } from '../sim/world.ts';

/** 알갱이의 종류. 앞의 다섯은 `World.fx`의 종류와 같은 차례다(`GRAIN_KINDS`). `hoof`는 말발굽(`hoof.ts`). */
export type GrainKind = 'cut' | 'thrust' | 'arrowOut' | 'arrowHit' | 'fall' | 'hoof';
export const GRAIN_KINDS: readonly GrainKind[] = ['cut', 'thrust', 'arrowOut', 'arrowHit', 'fall'];

export interface GrainReq {
  kind: GrainKind;
  /** 크기(0~1). */
  gain: number;
  /** 좌우(-1 왼쪽 ~ 1 오른쪽). */
  pan: number;
  /** 이 틱 안에서 늦출 시간(ms) — 같은 틱의 알갱이가 한 점에 겹치지 않게 흩는다. */
  delayMs: number;
  /** 음높이 배율(1 근처). 같은 소리가 되풀이되어도 기계처럼 들리지 않게. */
  pitch: number;
}

/** 한 자리가 어디서 들리는가: 화면의 좌우(-1~1)와 보는 사람에게서의 거리(전장 px) — `Renderer.hear`. */
export interface Hearing {
  pan: number;
  dist: number;
}
export type Hear = (xPx: number, yPx: number, out: Hearing) => void;

export interface MixSettings {
  /** 종류마다 한 틱에 낼 수 있는 알갱이. */
  cap: number;
  /** 타격 몇 번을 한 알갱이로 묶는가. */
  group: number;
  /** `WINDOW_MS` 안에 낼 수 있는 알갱이(전체). */
  win: number;
  /** 깔개의 크기 배율. */
  bed: number;
}

/**
 * 플레이한 사람이 고른 잦기 — "적게"(2026-10-08): 종류마다 틱에 하나, 타격 넷을 한 소리로, 0.1초에 다섯까지.
 * "보통"(틱에 둘·셋을 하나로·여덟)과 "많이"(셋·둘·열둘)도 들어 봤다. 깔개의 크기는 "보통"(1).
 */
export const MIX_DEFAULT: MixSettings = { cap: 1, group: 4, win: 5, bed: 1 };

export const WINDOW_MS = 100;
/** 이 거리(전장 px)부터 알갱이가 가장 작다. 화면 밖 먼 싸움은 깔개에 맡긴다. */
export const FAR_PX = 900;
export const FAR_GAIN = 0.25;
/** 밤의 알갱이 크기(거리와 무관). 가까운 것과 먼 것의 가운데쯤. */
export const FLAT_GAIN = 0.6;
/** 좌우의 끝. 한쪽 귀로만 몰지 않는다. */
export const PAN_MAX = 0.8;
/** 깔개가 가장 커지는 타격의 잦기(틱당, 고르게 한 값). */
export const BED_FULL = 2.5;
const BED_EASE = 0.06;
const TICK_MS = 1000 / 30;

export class BattleMix {
  settings: MixSettings;
  /** 자리를 싣지 않는다(밤): 좌우는 가운데, 크기는 거리와 무관하게 `FLAT_GAIN`. */
  flat = false;
  private readonly recent: number[] = [];
  private ema = 0;
  private readonly out: GrainReq[] = [];
  private readonly at: Hearing = { pan: 0, dist: 0 };
  private readonly rng: () => number;

  constructor(settings: MixSettings = MIX_DEFAULT, rng: () => number = Math.random) {
    this.settings = settings;
    this.rng = rng;
  }

  /** 지금의 깔개 크기(0~1 × 배율). `step` 뒤에 읽는다. */
  get bedLevel(): number {
    return Math.min(1, this.ema / BED_FULL) * this.settings.bed;
  }

  /**
   * 한 틱의 셈을 알갱이로 바꾼다. `nowMs`는 벽시계(전체 상한을 재는 데 쓴다), `speed`는 다시 보기의 배속 —
   * 2배면 같은 시간에 틱이 두 배로 지나가므로 틱당 낼 수를 그만큼 줄인다(하나 밑으로는 줄이지 않는다 — 지금의
   * "적게"는 이미 하나라 2배에서도 하나다. 그때 밀도를 잡는 것은 시간 창의 전체 상한이다). 돌려준 배열은 다음 부름에 다시 쓴다.
   */
  step(fx: Int32Array, nowMs: number, hear: Hear | null, speed = 1): readonly GrainReq[] {
    const out = this.out;
    out.length = 0;
    const s = this.settings;
    // 깔개는 맞은 것만 센다(베기·찌르기·화살이 맞음). 쏜 화살과 쓰러짐은 잦기가 아니다.
    const busy = fx[FX.Cut * 3] + fx[FX.Thrust * 3] + fx[FX.ArrowHit * 3];
    this.ema += (busy - this.ema) * BED_EASE;
    const recent = this.recent;
    while (recent.length > 0 && recent[0] <= nowMs - WINDOW_MS) recent.shift();
    const perTick = Math.max(1, Math.round(s.cap / Math.max(1, speed)));
    for (let k = 0; k < FX_KINDS; k++) {
      const n = fx[k * 3];
      if (n === 0) continue;
      let pan = 0;
      let near = 1;
      if (this.flat) near = FLAT_GAIN;
      else if (hear) {
        hear(fx[k * 3 + 1] / n, fx[k * 3 + 2] / n, this.at);
        pan = this.at.pan;
        near = Math.max(FAR_GAIN, 1 - (this.at.dist / FAR_PX) * (1 - FAR_GAIN));
      }
      const count = Math.min(perTick, Math.ceil(n / s.group));
      for (let j = 0; j < count; j++) {
        if (recent.length >= s.win) return out;
        recent.push(nowMs);
        // 여럿이 한꺼번에 부딪칠수록 조금 크게. 쓰러짐은 드물고 무거워 한 단계 크게 낸다.
        const base = Math.min(1, 0.5 + 0.18 * Math.sqrt(n) + (k === FX.Fall ? 0.2 : 0));
        const spread = this.flat ? 0 : (this.rng() - 0.5) * 0.3;
        out.push({
          kind: GRAIN_KINDS[k],
          gain: base * near,
          pan: Math.max(-PAN_MAX, Math.min(PAN_MAX, pan * PAN_MAX + spread)),
          delayMs: this.rng() * TICK_MS,
          pitch: 0.92 + this.rng() * 0.16,
        });
      }
    }
    return out;
  }

  /** 판이 바뀌었다(새 판·되감기): 기억을 비운다. */
  reset(): void {
    this.recent.length = 0;
    this.ema = 0;
  }
}
